import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { AxiosResponse } from "axios";
import { GetEventDetail, GetRaceEventDetail, marketService } from "src/api/analysisApi";
import { EventDetail, EventDetailResponse, RaceEventDetailI } from "src/types/eventDetail";
import { RootState } from "../store";

export const getEventById = createAsyncThunk(
  "market/getEventById",
  async (payload: GetEventDetail, { rejectWithValue }) => {
    try {
      const { data }: AxiosResponse<EventDetailResponse> = await marketService.getMarketById(payload);

      return data.data;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const getRaceMarketDetail = createAsyncThunk(
  "market/getRaceMarketDetail",
  async (payload: GetRaceEventDetail, { rejectWithValue }) => {
    try {
      const { data } = await marketService.getRaceMarketById(payload);

      return {
        raceEvent: data?.data?.market_details || null,
        raceMarkets: Array.isArray(data?.data?.market_list) ? data.data.market_list : [],
      };
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const fetchEventExposure = createAsyncThunk(
  "event/fetchEventExposure",
  async (member_id: string, thunkAPI) => {
    try {
      const data = await marketService.getAdminNetExposure(member_id);

      // Use Map to preserve insertion order
      const groupedMap = new Map();

      if (Array.isArray(data.data)) {
        data.data.forEach((item: any) => {
          const key = item.provider_game_id;
          if (!groupedMap.has(key)) {
            groupedMap.set(key, []);
          }
          groupedMap.get(key).push(item);
        });
      }

      // Convert Map values to array of arrays
      const result = Array.from(groupedMap.values());

      console.log("🚀 ~ fetchEventExposure data.data:", data.data);
      return {
        allEvents: Array.isArray(data.data) ? data.data : [],
        groupedEvents: result,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data?.status_message || "Failed to fetch event exposure");
    }
  }
);

interface MarketStateI {
  loading: boolean;
  event: EventDetail | null;
  raceEvent: RaceEventDetailI | null;
  raceMarkets: any[];

  allEvents: any[];
  groupedEventsExposure: any[];
}

const initialState: MarketStateI = {
  loading: false,
  event: null,
  raceEvent: null,
  raceMarkets: [],

  allEvents: [],
  groupedEventsExposure: [],
};

const eventSlice = createSlice({
  name: "event",
  initialState,
  reducers: {
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    resetState: () => {
      return { ...initialState };
    },
    resetEvent: (state) => {
      state.event = null;
      state.raceEvent = null;
      state.raceMarkets = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEventById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEventById.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.event = payload ?? {};
      })
      .addCase(getEventById.rejected, (state) => {
        state.loading = false;
        state.event = null;
      });
    builder
      .addCase(getRaceMarketDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRaceMarketDetail.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.raceEvent = payload?.raceEvent;
        state.raceMarkets = payload?.raceMarkets;
      })
      .addCase(getRaceMarketDetail.rejected, (state) => {
        state.loading = false;
        state.raceEvent = null;
        state.raceMarkets = [];
      });
    builder
      // .addCase(fetchEventExposure.pending, (state) => {
      //   state.loading = true;
      // })
      .addCase(fetchEventExposure.fulfilled, (state, action) => {
        console.log("🚀 ~ fetchEventExposure action:", action)
        state.allEvents = action.payload.allEvents;
        state.groupedEventsExposure = action.payload.groupedEvents;
      });
    // .addCase(fetchEventExposure.rejected, (state) => {
    //   state.loading = false;
    // });
  },
});

export const eventAction = eventSlice.actions;
export default eventSlice.reducer;

export const selectEventState = (state: RootState) => state.event;
